import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { errorNotificationManager } from './errorNotificationService';
import i18n from '../i18n/config';

// Translate outside of React. i18n.t respects the user's selected language
// (persisted in localStorage) and falls back to English.
const te = (key: string) => i18n.t(key);

const VITE_API_URL = import.meta.env.VITE_API_URL || '';
const VITE_API_VERSION = import.meta.env.VITE_API_VERSION || 'v1';

export const getAuthToken = (): string | null => {
  // Prefer the token for the current user type, but fall back for older sessions
  const userType = localStorage.getItem('user_type');
  const employeeToken = localStorage.getItem('employee_token');
  const agencyToken = localStorage.getItem('agency_token');
  const directToken = localStorage.getItem('token');

  const storedAuth = localStorage.getItem('agency-auth-storage');
  let persistedToken: string | null = null;

  if (storedAuth) {
    try {
      const parsed = JSON.parse(storedAuth);
      persistedToken = parsed?.state?.token || null;
    } catch {
      persistedToken = null;
    }
  }

  if (userType === 'employee') {
    return employeeToken || agencyToken || persistedToken || directToken;
  }

  return agencyToken || employeeToken || persistedToken || directToken;
};

// Create axios instance with default config
const apiClient: AxiosInstance = axios.create({
  baseURL: `${VITE_API_URL}/api/${VITE_API_VERSION}`,
  timeout: 30000,
  headers: {
    // Prevent intermediate caches from serving stale GET responses
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  },
});

// Request interceptor - Add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Transient failures (no-response and 5xx from the reverse proxy) commonly
// flash for a few seconds during a deploy, rolling restart, or brief network
// blip. Showing the toast immediately makes the app feel broken every time the
// backend is updated. Instead, schedule the toast 10s out and cancel it as
// soon as any request succeeds — only sustained outages reach the user.
const TRANSIENT_ERROR_DEBOUNCE_MS = 10000;

let pendingNetworkErrorTimer: ReturnType<typeof setTimeout> | null = null;
let pendingServerErrorTimer: ReturnType<typeof setTimeout> | null = null;
let lastNetworkToastAt = 0;
let lastServerToastAt = 0;

const scheduleNetworkErrorToast = () => {
  if (pendingNetworkErrorTimer !== null) return;
  pendingNetworkErrorTimer = setTimeout(() => {
    pendingNetworkErrorTimer = null;
    const now = Date.now();
    if (now - lastNetworkToastAt < TRANSIENT_ERROR_DEBOUNCE_MS) return;
    lastNetworkToastAt = now;
    errorNotificationManager.showCritical(
      te('errorHandling.network.title'),
      te('errorHandling.network.message'),
      { duration: 8000 }
    );
  }, TRANSIENT_ERROR_DEBOUNCE_MS);
};

const scheduleServerErrorToast = () => {
  if (pendingServerErrorTimer !== null) return;
  pendingServerErrorTimer = setTimeout(() => {
    pendingServerErrorTimer = null;
    const now = Date.now();
    if (now - lastServerToastAt < TRANSIENT_ERROR_DEBOUNCE_MS) return;
    lastServerToastAt = now;
    errorNotificationManager.showCritical(
      te('errorHandling.server.title'),
      te('errorHandling.server.message'),
      { duration: 8000 }
    );
  }, TRANSIENT_ERROR_DEBOUNCE_MS);
};

const clearPendingTransientErrors = () => {
  if (pendingNetworkErrorTimer !== null) {
    clearTimeout(pendingNetworkErrorTimer);
    pendingNetworkErrorTimer = null;
  }
  if (pendingServerErrorTimer !== null) {
    clearTimeout(pendingServerErrorTimer);
    pendingServerErrorTimer = null;
  }
};

// URLs that legitimately return 404 in the normal flow (caller already handles
// it). Without this list, a fresh tenant with no subscription or an agency
// without notifications enabled would see a stray "Resource Not Found" toast.
const SILENT_404_PATTERNS = [
  /\/receipt/,
  /\/subscription/,
  /\/agency\/notifications/,
];

// Response interceptor - Handle errors globally
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // A successful response means the backend is reachable again — drop any
    // pending transient-error toasts so we don't fire after recovery.
    clearPendingTransientErrors();
    return response;
  },
  (error: AxiosError) => {
    // Requests aborted by the caller (unmount, signal abort) aren't failures.
    if (axios.isCancel(error) || (error as any).code === 'ERR_CANCELED') {
      return Promise.reject(error);
    }

    if (error.response) {
      // Server responded with error status
      const status = error.response.status;

      if (status === 401) {
        const skipAuthRedirect = (error.config as any)?.skipAuthRedirect;
        if (skipAuthRedirect) {
          return Promise.reject(error);
        }

        // Unauthorized - clear token and redirect to login
        // Only redirect if not already on login/register pages
        const currentPath = window.location.pathname;
        const userType = localStorage.getItem('user_type');

        if (currentPath !== '/login' && currentPath !== '/register' && currentPath !== '/employee/login') {
          if (userType === 'employee') {
            localStorage.removeItem('employee_token');
            localStorage.removeItem('employee_refresh_token');
            localStorage.removeItem('employee_user');
            localStorage.removeItem('user_type');
            window.location.href = '/employee/login';
          } else {
            localStorage.removeItem('agency_token');
            localStorage.removeItem('agency_user');
            localStorage.removeItem('user_type');
            window.location.href = '/login';
          }
        }
      } else if (status === 403) {
        // Forbidden
        errorNotificationManager.showWarning(
          te('errorHandling.forbidden.title'),
          te('errorHandling.forbidden.message'),
          { duration: 6000 }
        );
      } else if (status === 404) {
        const url = error.config?.url || '';
        const isSilent = SILENT_404_PATTERNS.some((pattern) => pattern.test(url));
        if (!isSilent) {
          errorNotificationManager.showInfo(
            te('errorHandling.resourceNotFound.title'),
            te('errorHandling.resourceNotFound.message'),
            { duration: 5000 }
          );
        }
      } else if (status >= 500) {
        // Gateway errors (502/503/504) and brief 500s during a backend redeploy
        // are usually gone within seconds — defer the toast and let a recovery
        // response cancel it.
        scheduleServerErrorToast();
      }
    } else if (error.request) {
      // Request made but no response (axios timeout, dropped connection,
      // proxy rolling over during a push). Defer the toast.
      scheduleNetworkErrorToast();
    } else {
      // Something else happened
      errorNotificationManager.showError(
        te('errorHandling.request.title'),
        error.message || te('errorHandling.request.message'),
        { duration: 6000 }
      );
    }

    return Promise.reject(error);
  }
);

// Helper function to handle API responses
export const handleApiResponse = <T>(response: AxiosResponse<T>): T => {
  return response.data;
};

// Helper function to handle API errors
export const handleApiError = (error: any): string => {
  // 1. Check for detailed validation errors first
  if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
    return error.response.data.errors
      .map((e: any) => {
        const msg = e.msg || e.message || (typeof e === 'string' ? e : '');
        const field = e.path || e.param || e.field;
        return field ? `${field}: ${msg}` : msg;
      })
      .filter(Boolean)
      .join(', ');
  }

  // 2. Check for a single error message from the server
  if (error.response?.data?.message) {
    const message = error.response.data.message;
    // If it's a generic "Validation failed" message and we already handled errors above,
    // this branch won't be reached if we prioritized correctly.
    return message;
  }

  // 3. Check for axios error message
  if (error.message) {
    if (error.message === 'Network Error') {
      return 'Unable to connect to the server. Please check your internet connection.';
    }
    return error.message;
  }

  return 'An unexpected error occurred';
};

export { apiClient };
export default apiClient;
